(() => {
  if (customElements.get('free-shipping-bar')) return;

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.messageEl = this.querySelector('.fs-bar__message');
      this.fillEl = this.querySelector('.fs-bar__fill');
      this.cart = null;
      this.tiers = null; // null = not loaded / unavailable; [] = loaded, no tiers
      this.refresh = this.refresh.bind(this);
      this.scheduleRefresh = () => {
        setTimeout(this.refresh, 800);
        setTimeout(this.refresh, 2500);
      };
      this.onSubmit = (event) => {
        const form = event.target;
        if (
          form &&
          form.matches &&
          (form.matches('form[action*="/cart/add"]') || form.matches('form[data-type="add-to-cart-form"]'))
        ) {
          this.scheduleRefresh();
        }
      };
      this.onChange = (event) => {
        const input = event.target;
        if (!input || !input.matches) return;
        if (input.matches('input[name="updates[]"], quantity-input input:not([name="quantity"])')) {
          this.scheduleRefresh();
        } else if (input.matches('input[name="quantity"]')) {
          this.render(); // PDP quantity selector: recompute locally, no fetch
        }
      };
      this.onClick = (event) => {
        if (event.target && event.target.closest && event.target.closest('cart-remove-button')) {
          this.scheduleRefresh();
        }
      };
      window.addEventListener('pageshow', this.refresh);
      window.addEventListener('focus', this.refresh);
      document.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('change', this.onChange, true);
      document.addEventListener('input', this.onChange, true);
      document.addEventListener('click', this.onClick, true);
      this.refresh();
      if (this.dataset.productId) this.loadTiers();
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.refresh);
      window.removeEventListener('focus', this.refresh);
      document.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('change', this.onChange, true);
      document.removeEventListener('input', this.onChange, true);
      document.removeEventListener('click', this.onClick, true);
    }

    // Tier source: the Quantity-X app proxy the bundle widget itself renders
    // from. Percentage tiers keyed by minimum quantity. On any failure the
    // bar keeps tiers = null and render() falls back to the live cart total
    // only — never an undiscounted prospective figure.
    async loadTiers() {
      try {
        const shop = window.Shopify && window.Shopify.shop;
        if (!shop) return;
        const url =
          '/apps/quantity-x/api/widget_data?product_id=' +
          encodeURIComponent(this.dataset.productId) +
          '&variant_id=null&is_preview=false&shop=' +
          encodeURIComponent(shop) +
          '&is_b2b=false&locale=en&customer_tags=';
        const data = await (await fetch(url, { headers: { Accept: 'application/json' } })).json();
        const offer = data && data.offer;
        if (
          offer &&
          offer.active &&
          offer.type === 'volume_discount' &&
          offer.value_type === 'percentage' &&
          Array.isArray(offer.tiers)
        ) {
          this.tiers = offer.tiers
            .map((tier) => ({
              minQty: Number(tier.conditions && tier.conditions.count),
              percent: Number(tier.conditions && tier.conditions.amount),
            }))
            .filter((tier) => tier.minQty > 0 && tier.percent >= 0 && tier.percent < 100)
            .sort((a, b) => a.minQty - b.minQty);
          this.render();
        }
      } catch (error) {
        /* tiers stay null; cart-total fallback applies */
      }
    }

    tierPercent(quantity) {
      let percent = 0;
      for (const tier of this.tiers) {
        if (quantity >= tier.minQty) percent = tier.percent;
      }
      return percent;
    }

    selectedQuantity() {
      const input = document.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]');
      const value = input ? parseInt(input.value, 10) : NaN;
      return value > 0 ? value : null;
    }

    // Prospective position (PDP mode): what the cart total becomes if the
    // selected quantity is added, with the combined quantity of this product
    // priced at the applicable tier. Requires loaded tiers and a variant
    // price; otherwise the live cart total is shown unmodified.
    prospectiveTotalCents() {
      const cartTotal = this.cart.total_price;
      if (!this.dataset.productId || this.tiers === null) return cartTotal;
      const unitPrice = parseInt(this.dataset.variantPrice, 10);
      const selected = this.selectedQuantity();
      if (!unitPrice || !selected) return cartTotal;
      const productId = Number(this.dataset.productId);
      let productLineCents = 0;
      let productQty = 0;
      for (const item of this.cart.items) {
        if (item.product_id === productId) {
          productLineCents += item.final_line_price;
          productQty += item.quantity;
        }
      }
      const combinedQty = productQty + selected;
      const percent = this.tierPercent(combinedQty);
      const combinedLineCents = Math.round(combinedQty * unitPrice * (1 - percent / 100));
      return cartTotal - productLineCents + combinedLineCents;
    }

    async refresh() {
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        this.cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      } catch (error) {
        this.hidden = true;
        return;
      }
      this.render();
    }

    // Threshold per currency, from the data-thresholds map. Doctrine
    // (9 Aug 2026): all entries are "gbp" — GBP-denominated like the zone
    // conditions themselves, converted at the live rate so the display
    // tracks what checkout evaluates. "fixed" mode is retained only for a
    // hypothetical future zone that is natively presentment-denominated.
    // No entry -> no bar.
    thresholdCents() {
      let map;
      try {
        map = JSON.parse(this.dataset.thresholds || '{}');
      } catch (error) {
        return null;
      }
      const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      const entry = currency ? map[currency] : null;
      if (!entry) return null;
      if (entry.fixed > 0) return Math.round(entry.fixed * 100);
      if (entry.gbp > 0) {
        const rate = parseFloat(window.Shopify.currency.rate);
        if (!rate) return null;
        return Math.round(entry.gbp * rate * 100);
      }
      return null;
    }

    render() {
      if (!this.cart) return;
      const shopifyCurrency = window.Shopify && window.Shopify.currency;
      const currency = shopifyCurrency && shopifyCurrency.active;
      const thresholdCents = this.thresholdCents();
      if (!currency || !thresholdCents) {
        this.hidden = true;
        return;
      }
      const cartCents = this.cart.total_price;
      const prospectiveCents = this.prospectiveTotalCents();
      const format = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
      });

      // Three states. Achieved language is reserved for the cart alone
      // qualifying; a crossing that still needs the add-to-cart click is an
      // invitation, not an achievement (task 15 v3 — the v2 defect was
      // achieved copy on a conditional state, suppressing the very
      // add-to-cart the bar exists to drive).
      if (cartCents >= thresholdCents) {
        this.messageEl.textContent =
          this.dataset.msgQualifies || 'Your order qualifies for free shipping';
        this.fillEl.style.width = '100%';
      } else if (prospectiveCents >= thresholdCents) {
        this.messageEl.textContent =
          this.dataset.msgUnlock || 'Add to your cart to unlock free shipping';
        this.fillEl.style.width = `${Math.min(99, (cartCents / thresholdCents) * 100)}%`;
      } else {
        const remainingCents = thresholdCents - prospectiveCents;
        this.messageEl.textContent = (
          this.dataset.msgAway || "You're [amount] away from free shipping"
        ).replace('[amount]', format.format(remainingCents / 100));
        this.fillEl.style.width = `${Math.min(99, (prospectiveCents / thresholdCents) * 100)}%`;
      }
      this.hidden = false;
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
})();
